import { Stack, Typography } from "@mui/material";
import AcxMainTextField from "components/UI/AcxMainTextField";
import theme from "Theme/AppTheme";
import React, { useState } from "react";
import AcxButton from "components/UI/AcxButton";
import { AcxMessageBox as MessageBox } from "components/UI/AcxMessageBox";
import { useNavigate } from "react-router";
import { delay } from "utils/helpers";

let search = window.location.search;
let params = new URLSearchParams(search);

const ruleChecks: Array<{ rule: RegExp; message: string }> = [
    {
        rule: /.{8,}/,
        message: "Password must be at least 8 characters long.",
    },
    {
        rule: /[a-z]/,
        message: "Password must contain at least one lowercase letter.",
    },
    {
        rule: /[A-Z]/,
        message: "Password must contain at least one uppercase letter.",
    },
    {
        rule: /\d/,
        message: "Password must contain at least one digit.",
    },
    {
        rule: /[^a-zA-Z0-9]/,
        message: "Password must contain at least one special character.",
    },
];

const getPasswordErrors = (password: string) =>
    ruleChecks
        .filter(({ rule }) => !rule.test(password))
        .map(({ message }) => message);

export default function ChangePassword() {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const [usernameError, setUsernameError] = useState("");
    const [passwordErrorMessages, setPasswordErrorMessages] = useState<
        string[]
    >([]);
    const [confirmPasswordError, setConfirmPasswordError] = useState("");
    const [needsNewReset, setNeedsNewReset] = useState(false);
    const [resetSuccess, setResetSuccess] = useState(false);
    const navigate = useNavigate();

    const allErrorMessages = [
        usernameError,
        ...passwordErrorMessages,
        confirmPasswordError,
        ...errorMessages,
    ].filter(Boolean); // Filter out any empty strings

    const updatePassword = async () => {
        let hasError = false;
        if (!username) {
            setUsernameError("Username is required.");
            hasError = true;
        }

        const passwordErrors = getPasswordErrors(password);
        if (passwordErrors.length > 0) {
            setPasswordErrorMessages(passwordErrors);
            hasError = true;
        }

        if (password !== confirmPassword) {
            setConfirmPasswordError("Passwords do not match.");
            hasError = true;
        }

        if (hasError) {
            return;
        }

        setIsLoading(true);

        const retUrl = params.get("ReturnUrl");
        const token = params.get("token");

        try {
            const response = await fetch(`api/Authenticate/UpdatePassword`, {
                method: "Post",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    UserName: username,
                    Password: password,
                    ReturnUrl: retUrl,
                    Token: token,
                }),
            });
            const data = await response.json();
            if (response.status === 200) {
                setResetSuccess(true);
                // Delay the redirect to allow the user to see the success message
                await delay(3500);
                navigate(
                    `/authentication/login?returnUrl=${encodeURIComponent(
                        window.location.origin,
                    )}/app`,
                );
            } else if (response.status === 400) {
                // If the token is invalid or expired the user needs to get a new reset link
                if (
                    data.UserName?.some?.((msg) =>
                        msg.includes("does not match the token"),
                    ) ||
                    data.Token?.some?.((msg) =>
                        msg.includes("reset password token"),
                    )
                ) {
                    setNeedsNewReset(true);
                }
                // I haven't seen these errors but left them since they were in the original code
                if (data.otherDetails === "UserName") {
                    setErrorMessages((errs) => [...errs, data.data.message]);
                } else if (data.otherDetails === "Password") {
                    setErrorMessages((errs) => [...errs, data.message]);
                }
            } else {
                setErrorMessages((errs) => [
                    ...errs,
                    "A general error occurred. Please contact the system administrator.",
                ]);
            }
        } catch (error) {
            console.log(error);
        }
        setIsLoading(false);
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            event.preventDefault();
            updatePassword();
        }
    };

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newUsername = e.target.value;
        setUsername(newUsername);
        if (usernameError && newUsername) {
            setUsernameError("");
        }
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        if (passwordErrorMessages.length > 0) {
            setPasswordErrorMessages(getPasswordErrors(newPassword));
        }
    };

    const handleConfirmPasswordChange = (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const newConfirmPassword = e.target.value;
        setConfirmPassword(newConfirmPassword);
        if (confirmPasswordError && newConfirmPassword === password) {
            setConfirmPasswordError("");
        }
    };

    return (
        <Stack
            component="form"
            onKeyDown={handleKeyPress}
            sx={{ gap: 1, width: "100%" }}
        >
            <AcxMainTextField
                labelText="Username"
                labelSx={{
                    fontSize: "14px",
                    mb: 1,

                    "&.Mui-focused": {
                        color: theme.palette.gray[700],
                        fontWeight: 400,
                    },
                }}
                id="reset-password-username"
                type="text"
                fullWidth
                value={username}
                onChange={handleUsernameChange}
                textContainerSx={{ maxHeight: "36px" }}
            />
            <AcxMainTextField
                labelText="New Password"
                labelSx={{
                    fontSize: "14px",
                    mb: 1,

                    "&.Mui-focused": {
                        color: theme.palette.gray[700],
                        fontWeight: 400,
                    },
                }}
                id="reset-password-password"
                type="password"
                fullWidth
                value={password}
                onChange={handlePasswordChange}
                textContainerSx={{ maxHeight: "36px" }}
                showPasswordToggle
                autoComplete="off"
            />
            <AcxMainTextField
                labelText="Confirm New Password"
                labelSx={{
                    fontSize: "14px",
                    mb: 1,

                    "&.Mui-focused": {
                        color: theme.palette.gray[700],
                        fontWeight: 400,
                    },
                }}
                id="reset-password-confirm-password"
                type="password"
                fullWidth
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                textContainerSx={{ maxHeight: "36px" }}
                showPasswordToggle
            />
            {allErrorMessages.length === 1 && (
                <MessageBox
                    sx={{
                        mt: 1.5,
                    }}
                    variant="error"
                >
                    {allErrorMessages[0]}
                </MessageBox>
            )}
            {allErrorMessages.length > 1 && (
                <Stack>
                    <MessageBox
                        sx={{
                            mt: 1.5,
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                        }}
                        variant="error"
                    >
                        Please address the following errors:
                    </MessageBox>
                    <Stack
                        component="ul"
                        sx={{
                            pl: 4,
                            pr: 2,
                            py: 1,
                            mt: 0,
                            background: theme.palette.error[100],
                            borderBottomLeftRadius: 4,
                            borderBottomRightRadius: 4,
                        }}
                    >
                        {allErrorMessages.map((msg, index) => (
                            <Typography
                                sx={{
                                    color: theme.palette.error[700],
                                    fontWeight: 600,
                                }}
                                component="li"
                                key={index}
                            >
                                {msg}
                            </Typography>
                        ))}
                    </Stack>
                </Stack>
            )}
            {needsNewReset && (
                <Stack>
                    <MessageBox
                        sx={{
                            mt: 1.5,
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                        }}
                    >
                        The reset link has expired or is invalid.
                    </MessageBox>
                    <Typography
                        component={"a"}
                        href="/reset"
                        sx={{
                            background: theme.palette.primary[100],
                            color: theme.palette.primary[700],
                            borderBottomLeftRadius: 4,
                            borderBottomRightRadius: 4,
                            width: "100%",
                            textAlign: "center",
                            pb: 1,
                            fontSize: "14px",
                        }}
                    >
                        Please request a new password reset.
                    </Typography>
                </Stack>
            )}
            {resetSuccess && (
                <MessageBox sx={{ mt: 1.5 }} variant="success">
                    Your password has been successfully reset. Redirecting you
                    to the login page...
                </MessageBox>
            )}

            {!needsNewReset && (
                <AcxButton
                    fullWidth
                    color="primary"
                    onClick={updatePassword}
                    sx={{
                        height: "36px",
                        mt: 1.5,
                    }}
                    loading={isLoading || resetSuccess}
                >
                    Reset password
                </AcxButton>
            )}
        </Stack>
    );
}
